import { Badge, Link } from 'maeng-daisyui'
import { useMemo } from 'react'
import classNames from 'classnames'
import { graphql, useStaticQuery } from 'gatsby'
import Message from 'maeng-utils/locale'
import profileGif from '@/assets/images/portfolio/profile.gif'

type SocialNetwork = {
  site: {
    siteMetadata: {
      name: string
      social: {
        github: string
        facebook: string
        linkedin: string
        instagram: string
        email: string
        blog: string
      }
    }
  }
}

const BusinessCard = () => {
  const {
    site: {
      siteMetadata: { name, social },
    },
  } = useStaticQuery<SocialNetwork>(graphql`
    query SocialNetwork {
      site {
        siteMetadata {
          social {
            github
            facebook
            linkedin
            instagram
            email
            blog
          }
        }
      }
    }
  `)

  const contacts = useMemo(() => {
    const SHOWN_LIST = ['email', 'github', 'blog', 'linkedin']

    return [
      {
        key: 'email',
        label: 'Email',
        link: `mailto:${social.email}`,
        content: social.email,
      },
      {
        key: 'github',
        label: 'Github',
        link: `https://github.com/${social.github}`,
        linkProps: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      },
      {
        key: 'blog',
        label: 'Blog',
        link: social.blog,
        linkProps: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      },
      {
        key: 'linkedin',
        label: 'LinkedIn',
        link: `https://www.linkedin.com/in/${social.linkedin}`,
        linkProps: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      },
      {
        key: 'instagram',
        label: 'Instagram',
        link: `https://www.instagram.com/${social.instagram}`,
        linkProps: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      },
      {
        key: 'facebook',
        label: 'Facebook',
        link: `https://www.facebook.com/${social.facebook}`,
        linkProps: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      },
    ].filter(({ key }) => SHOWN_LIST.includes(key))
  }, [social])

  return (
    <section className={classNames('mb-4', 'md:mb-12')}>
      <Message
        i18nKey="portfolio.businessCard.title"
        className="text-3xl font-extrabold py-5"
        component="h2"
      />
      <div className={classNames('block mx-4', 'md:flex')}>
        <div
          className={classNames(
            'w-1/2 h-auto min-w-[13rem] rounded-xl border border-solid border-[#f0f0f0] shadow-xl',
            'md:w-64 md:h-64',
          )}
        >
          <img
            className="aspect-square object-cover rounded-xl border border-solid border-[#f0f0f0] shadow-xl"
            src={profileGif}
            alt="Profile"
          />
        </div>
        <div className={classNames('pl-4 pt-8', 'md:pl-8 md:pt-0')}>
          <h3
            className={classNames(
              'text-2xl font-extrabold semi-highlight-[#ffaf19] mb-4',
              'md:mb-5',
            )}
          >
            {name}
          </h3>
          <ul className={classNames('flex flex-col gap-6 pt-4', 'md:gap-4 md:pt-0')}>
            {contacts.map((contact, index) => (
              <li
                className={classNames('inline-grid gap-2 align-middle', 'md:flex md:gap-4')}
                key={index}
              >
                <Badge
                  className="flex font-semibold w-fit min-w-[6rem]"
                  outlined
                  size="lg"
                  color="warning"
                >
                  {contact.label}
                </Badge>
                <Link
                  className={classNames('pl-4', 'md:pl-0 no-underline')}
                  color="info"
                  href={contact.link}
                  {...contact?.linkProps}
                >
                  {contact.content ?? contact.link}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  )
}

export default BusinessCard
